/**
 * Digital Twin Economy Dashboard — Control room for the autonomous economy.
 * Route: /twin
 * Shows: Economy health, live bounties, IoT devices, settlements, approvals, event stream.
 */
import { useState, useEffect, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Cpu, Zap, CheckCircle, Clock, AlertTriangle, Shield,
  Activity, DollarSign, RefreshCw, Loader2, CircleDot,
  Box, Wifi, WifiOff, ThumbsUp, ThumbsDown, Eye,
  ShieldCheck, ShieldAlert, Sparkles, Wrench, BedDouble,
  ArrowRight, Radio, Lock, Hash, ChevronDown, ChevronUp
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { BACKEND_URL } from '@/lib/backendUrl';

const DEPT_COLORS = {
  housekeeping: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: Sparkles },
  maintenance: { color: 'text-amber-400', bg: 'bg-amber-500/10', icon: Wrench },
  front_desk: { color: 'text-blue-400', bg: 'bg-blue-500/10', icon: BedDouble },
};

const PRIORITY_STYLES = {
  urgent: 'border-red-500/40 bg-red-500/5',
  high: 'border-orange-500/40 bg-orange-500/5',
  medium: 'border-amber-500/30 bg-amber-500/5',
  low: 'border-blue-500/30 bg-blue-500/5',
};

const TIER_LABELS = { 1: 'Auto', 2: 'Sage', 3: 'Owner' };
const TIER_COLORS = { 1: 'bg-emerald-500/10 text-emerald-400', 2: 'bg-purple-500/10 text-purple-400', 3: 'bg-red-500/10 text-red-400' };

function KpiCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-[#0d1220] border border-[#1a2235] rounded-xl px-4 py-3 flex items-center gap-3">
      <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", color)}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-2xl font-bold font-mono">{value}</p>
        <p className="text-[10px] text-[#64748b]">{label}</p>
        {sub && <p className="text-[8px] text-[#475569]">{sub}</p>}
      </div>
    </div>
  );
}

function BountyRow({ bounty }) {
  const dept = DEPT_COLORS[bounty.department] || DEPT_COLORS.front_desk;
  const DeptIcon = dept.icon;
  return (
    <div className={cn("border-l-2 rounded-r-lg px-3 py-2.5 flex items-start gap-2.5 transition-all",
      PRIORITY_STYLES[bounty.priority] || PRIORITY_STYLES.medium
    )} data-testid={`bounty-${bounty.id}`}>
      <div className={cn("w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5", dept.bg)}>
        <DeptIcon className={cn("w-3.5 h-3.5", dept.color)} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-semibold leading-snug">{bounty.title}</p>
        <div className="flex flex-wrap gap-1.5 mt-1">
          <Badge className={cn("text-[7px]", PRIORITY_STYLES[bounty.priority])}>{bounty.priority}</Badge>
          {bounty.room_number && <Badge className="text-[7px] bg-white/5 border-white/10">Room {bounty.room_number}</Badge>}
          {bounty.escalated && <Badge className="text-[7px] bg-purple-500/10 text-purple-400">Escalated</Badge>}
          <Badge className="text-[7px] bg-white/5 border-white/10">{bounty.source}</Badge>
        </div>
        {bounty.assigned_name && (
          <p className="text-[9px] text-[#475569] mt-1">Assigned: {bounty.assigned_name}</p>
        )}
      </div>
      <div className="shrink-0 text-right">
        <p className="text-sm font-bold font-mono text-amber-400">{bounty.hermes_reward}</p>
        <p className="text-[7px] text-[#475569]">BHERMES</p>
        <Badge className={cn("text-[7px] mt-1",
          bounty.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400' :
          bounty.status === 'open' ? 'bg-amber-500/10 text-amber-400' : 'bg-[#1a2235] text-[#475569]'
        )}>
          {bounty.status}
        </Badge>
      </div>
    </div>
  );
}

function SettlementRow({ s }) {
  return (
    <div className="flex items-center gap-2 py-2 border-b border-[#1a2235]/50 last:border-0">
      <div className="w-6 h-6 rounded-md bg-emerald-500/10 flex items-center justify-center shrink-0">
        <Zap className="w-3 h-3 text-emerald-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] truncate">{s.staff_name || 'System'}</p>
        <p className="text-[8px] text-[#475569]">{s.type}</p>
      </div>
      <p className="text-sm font-bold font-mono text-emerald-400 shrink-0">+{s.hermes_amount}</p>
      <div className="text-right shrink-0">
        <p className="text-[8px] text-[#475569] font-mono">{s.timestamp?.slice(11, 16)}</p>
        <p className="text-[7px] text-[#374151] font-mono flex items-center gap-0.5">
          <Lock className="w-2 h-2" /> {s.checksum?.slice(0, 8)}
        </p>
      </div>
    </div>
  );
}

function DeviceCard({ device }) {
  const isOnline = true; // In production, check last_event timestamp
  return (
    <div className="bg-[#080c16] border border-[#1a2235] rounded-lg px-3 py-2 flex items-center gap-2">
      <div className={cn("w-2 h-2 rounded-full", isOnline ? "bg-emerald-500 animate-pulse" : "bg-[#475569]")} />
      {isOnline ? <Wifi className="w-3 h-3 text-emerald-400" /> : <WifiOff className="w-3 h-3 text-[#475569]" />}
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-medium truncate">{device.device_id}</p>
        <p className="text-[8px] text-[#475569]">{device.device_type} | Room {device.room_number || '—'}</p>
      </div>
    </div>
  );
}

function ApprovalCard({ approval, onDecide }) {
  return (
    <div className="bg-[#080c16] border border-[#1a2235] rounded-xl px-3 py-2.5" data-testid={`approval-${approval.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <Badge className={cn("text-[7px]", TIER_COLORS[approval.trust_tier] || TIER_COLORS[2])}>
              Tier {approval.trust_tier}: {TIER_LABELS[approval.trust_tier]}
            </Badge>
            <Badge className="text-[7px] bg-amber-500/10 text-amber-400 animate-pulse">{approval.status}</Badge>
          </div>
          <p className="text-[11px] font-semibold mt-1">{approval.title}</p>
          <p className="text-[9px] text-[#64748b] mt-0.5">{approval.description?.slice(0, 80)}</p>
          <p className="text-[8px] text-[#475569] font-mono mt-1">{approval.requested_at?.slice(0, 16).replace('T', ' ')}</p>
        </div>
        <div className="flex gap-1 shrink-0">
          <Button size="sm" onClick={() => onDecide(approval.id, 'approved')}
            className="h-6 text-[8px] bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400 border border-emerald-500/20 px-2"
            data-testid={`approve-${approval.id}`}>
            <ThumbsUp className="w-2.5 h-2.5" />
          </Button>
          <Button size="sm" onClick={() => onDecide(approval.id, 'rejected')}
            className="h-6 text-[8px] bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 px-2"
            data-testid={`reject-${approval.id}`}>
            <ThumbsDown className="w-2.5 h-2.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function DigitalTwinDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [bounties, setBounties] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [devices, setDevices] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const hotelId = 'hotel_grand_marina';

  const fetchAll = useCallback(async () => {
    try {
      const [dashRes, bountyRes, settleRes, deviceRes, approvalRes, eventRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/twin/dashboard/${hotelId}`),
        fetch(`${BACKEND_URL}/api/twin/bounties/${hotelId}`),
        fetch(`${BACKEND_URL}/api/twin/settlements/${hotelId}?limit=20`),
        fetch(`${BACKEND_URL}/api/twin/devices/${hotelId}`),
        fetch(`${BACKEND_URL}/api/twin/approvals/${hotelId}?status=pending_sage`),
        fetch(`${BACKEND_URL}/api/twin/events/${hotelId}?limit=20`),
      ]);
      const [dash, bData, sData, dData, aData, eData] = await Promise.all([
        dashRes.json(), bountyRes.json(), settleRes.json(), deviceRes.json(), approvalRes.json(), eventRes.json(),
      ]);
      setDashboard(dash);
      setBounties(bData.bounties || []);
      setSettlements(sData.settlements || []);
      setDevices(dData.devices || []);
      setApprovals(aData.approvals || []);
      setEvents(eData.events || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [hotelId]);

  const decideApproval = async (id, decision) => {
    try {
      await fetch(`${BACKEND_URL}/api/twin/approvals/${id}/decide?decision=${decision}&decided_by=sage`, { method: 'POST' });
      fetchAll();
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchAll(); const iv = setInterval(fetchAll, 30000); return () => clearInterval(iv); }, [fetchAll]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080c16] flex items-center justify-center">
        <div className="flex items-center gap-3 text-[#64748b]">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm font-mono">Digital Twin loading...</span>
        </div>
      </div>
    );
  }

  if (!dashboard) return null;
  const { kpis } = dashboard;
  const openBounties = bounties.filter(b => b.status === 'open');
  const completedBounties = bounties.filter(b => b.status === 'completed');
  const pendingApprovals = approvals.filter(a => a.status === 'pending_sage' || a.status === 'pending_owner');

  return (
    <div className="min-h-screen bg-[#080c16] text-[#f1f5f9]" data-testid="digital-twin-dashboard">
      {/* Top Bar */}
      <div className="border-b border-[#1a2235] bg-[#0a0f1d]/80 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-[1800px] mx-auto px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-9 h-9 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                <Cpu className="w-4.5 h-4.5 text-cyan-400" />
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-[#0a0f1d] animate-pulse" />
            </div>
            <div>
              <h1 className="text-sm font-bold tracking-tight" data-testid="twin-title">Digital Twin Economy</h1>
              <p className="text-[9px] text-[#475569]">Autonomous Hospitality Intelligence</p>
            </div>
            <Badge className={cn("ml-2 text-[9px]",
              dashboard.economy_status === 'active'
                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
            )}>
              <Radio className="w-2.5 h-2.5 mr-1" /> {dashboard.economy_status === 'active' ? 'Economy Active' : 'Setup Needed'}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            {pendingApprovals.length > 0 && (
              <Badge className="text-[9px] bg-purple-500/10 text-purple-400 animate-pulse">
                <ShieldAlert className="w-2.5 h-2.5 mr-1" /> {pendingApprovals.length} pending
              </Badge>
            )}
            <Button size="sm" variant="ghost" onClick={fetchAll}
              className="h-7 text-[10px] text-[#64748b] hover:text-cyan-400 px-2" data-testid="twin-refresh">
              <RefreshCw className="w-3 h-3 mr-1" /> Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1800px] mx-auto p-4 space-y-4">

        {/* Row 1: KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="twin-kpis">
          <KpiCard icon={Box} label="Open Bounties" value={kpis.open_bounties} color="bg-amber-500/10 text-amber-400" />
          <KpiCard icon={CheckCircle} label="Completed" value={kpis.completed_bounties} color="bg-emerald-500/10 text-emerald-400" />
          <KpiCard icon={Zap} label="BHERMES Settled" value={kpis.total_hermes_settled} sub={`Today: ${kpis.today_hermes_settled}`} color="bg-cyan-500/10 text-cyan-400" />
          <KpiCard icon={Cpu} label="Active Devices" value={kpis.active_devices} sub={`Events today: ${kpis.events_today}`} color="bg-purple-500/10 text-purple-400" />
        </div>

        {/* Row 2: Bounties + Approvals + Settlements */}
        <div className="grid grid-cols-12 gap-4">

          {/* Live Bounties */}
          <div className="col-span-12 lg:col-span-5 bg-[#0d1220] border border-[#1a2235] rounded-xl p-4" data-testid="twin-bounties">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-bold uppercase tracking-wider text-[#94a3b8] flex items-center gap-1.5">
                <Box className="w-3.5 h-3.5" /> Live Bounties
              </h2>
              <div className="flex gap-1.5">
                <Badge className="text-[8px] bg-amber-500/10 text-amber-400">{openBounties.length} open</Badge>
                <Badge className="text-[8px] bg-emerald-500/10 text-emerald-400">{completedBounties.length} done</Badge>
              </div>
            </div>
            <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
              {bounties.length === 0 ? (
                <div className="text-center py-8 text-[#475569]">
                  <Box className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-[11px]">No bounties yet — IoT events will create them</p>
                </div>
              ) : (
                bounties.slice(0, 15).map(b => <BountyRow key={b.id} bounty={b} />)
              )}
            </div>
          </div>

          {/* Approval Queue */}
          <div className="col-span-12 lg:col-span-4 bg-[#0d1220] border border-[#1a2235] rounded-xl p-4" data-testid="twin-approvals">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-bold uppercase tracking-wider text-[#94a3b8] flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" /> Approval Queue
              </h2>
              <Badge className={cn("text-[8px]",
                pendingApprovals.length > 0 ? 'bg-purple-500/10 text-purple-400 animate-pulse' : 'bg-emerald-500/10 text-emerald-400'
              )}>
                {pendingApprovals.length > 0 ? `${pendingApprovals.length} pending` : 'All clear'}
              </Badge>
            </div>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {pendingApprovals.length === 0 ? (
                <div className="text-center py-8 text-[#475569]">
                  <ShieldCheck className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-[11px]">No pending approvals</p>
                </div>
              ) : (
                pendingApprovals.map(a => <ApprovalCard key={a.id} approval={a} onDecide={decideApproval} />)
              )}
            </div>
          </div>

          {/* BHERMES Settlements */}
          <div className="col-span-12 lg:col-span-3 bg-[#0d1220] border border-[#1a2235] rounded-xl p-4" data-testid="twin-settlements">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-bold uppercase tracking-wider text-[#94a3b8] flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5" /> Settlements
              </h2>
              <Badge className="text-[8px] bg-cyan-500/10 text-cyan-400">
                {kpis.total_hermes_settled} BHERMES
              </Badge>
            </div>
            <div className="space-y-0.5 max-h-[400px] overflow-y-auto">
              {settlements.length === 0 ? (
                <div className="text-center py-8 text-[#475569]">
                  <Zap className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-[11px]">No settlements yet</p>
                </div>
              ) : (
                settlements.map((s, i) => <SettlementRow key={i} s={s} />)
              )}
            </div>
          </div>
        </div>

        {/* Row 3: IoT Devices + Event Stream */}
        <div className="grid grid-cols-12 gap-4">

          {/* IoT Devices */}
          <div className="col-span-12 lg:col-span-4 bg-[#0d1220] border border-[#1a2235] rounded-xl p-4" data-testid="twin-devices">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-bold uppercase tracking-wider text-[#94a3b8] flex items-center gap-1.5">
                <Wifi className="w-3.5 h-3.5" /> IoT Devices
              </h2>
              <Badge className="text-[8px] bg-purple-500/10 text-purple-400">{devices.length} active</Badge>
            </div>
            <div className="space-y-1.5 max-h-[250px] overflow-y-auto">
              {devices.length === 0 ? (
                <div className="text-center py-6 text-[#475569]">
                  <Wifi className="w-6 h-6 mx-auto mb-2 opacity-30" />
                  <p className="text-[11px]">No devices registered</p>
                </div>
              ) : (
                devices.map((d, i) => <DeviceCard key={i} device={d} />)
              )}
            </div>
          </div>

          {/* Event Stream */}
          <div className="col-span-12 lg:col-span-8 bg-[#0d1220] border border-[#1a2235] rounded-xl p-4" data-testid="twin-events">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-bold uppercase tracking-wider text-[#94a3b8] flex items-center gap-1.5">
                <Activity className="w-3.5 h-3.5" /> Event Stream
              </h2>
              <Badge className="text-[8px] bg-cyan-500/10 text-cyan-400">{events.length} events</Badge>
            </div>
            <div className="space-y-1 max-h-[250px] overflow-y-auto font-mono text-[10px]">
              {events.length === 0 ? (
                <div className="text-center py-6 text-[#475569]">
                  <Activity className="w-6 h-6 mx-auto mb-2 opacity-30" />
                  <p className="text-[11px] font-sans">No events yet — IoT sensors will stream here</p>
                </div>
              ) : (
                events.map((e, i) => (
                  <div key={i} className="flex items-center gap-2 py-1 border-b border-[#1a2235]/30 last:border-0 hover:bg-white/[0.02]">
                    <span className="text-[#475569] shrink-0">{e.timestamp?.slice(11, 19)}</span>
                    <span className={cn("shrink-0",
                      e.source === 'iot' ? 'text-cyan-400' :
                      e.source === 'bitsy' ? 'text-blue-400' :
                      e.source === 'marina_ai' ? 'text-emerald-400' :
                      'text-[#64748b]'
                    )}>
                      [{e.source}]
                    </span>
                    <span className="text-amber-400 shrink-0">{e.event_type}</span>
                    {e.room_number && <span className="text-[#64748b]">Room {e.room_number}</span>}
                    {e.device_id && <span className="text-[#374151] truncate">{e.device_id}</span>}
                    <span className="ml-auto shrink-0">
                      {e.decision?.action === 'bounty_created' && <Badge className="text-[7px] bg-emerald-500/10 text-emerald-400">Bounty</Badge>}
                      {e.decision?.action?.includes('pending') && <Badge className="text-[7px] bg-purple-500/10 text-purple-400">Pending</Badge>}
                      {e.decision?.action === 'logged' && <Badge className="text-[7px] bg-[#1a2235] text-[#475569]">Logged</Badge>}
                      {e.decision?.action === 'blocked' && <Badge className="text-[7px] bg-red-500/10 text-red-400">Blocked</Badge>}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Trust Tier Legend */}
        <div className="flex items-center gap-4 justify-center py-2">
          <div className="flex items-center gap-1.5 text-[9px] text-[#475569]">
            <div className="w-2 h-2 rounded-full bg-emerald-500" /> Tier 1: Auto-execute (&lt;10 BHERMES)
          </div>
          <div className="flex items-center gap-1.5 text-[9px] text-[#475569]">
            <div className="w-2 h-2 rounded-full bg-purple-500" /> Tier 2: Sage Approval (10-500)
          </div>
          <div className="flex items-center gap-1.5 text-[9px] text-[#475569]">
            <div className="w-2 h-2 rounded-full bg-red-500" /> Tier 3: Owner Only (&gt;500)
          </div>
        </div>
      </div>
    </div>
  );
}
